import { useEffect, useState } from "react";
import { api, mxn, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Modal, Btn, Input, EmptyState, ConfirmDialog } from "@/components/Kit";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Power } from "lucide-react";

const empty = { name: "", sku: "", description: "", unit: "pieza", unit_price: 0, active: true };

export default function Productos() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(null);

  const load = () => api.get("/products").then((r) => setProducts(r.data));
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(empty);
    setEditing(null);
    setOpen(true);
  };
  const openEdit = (p) => {
    setForm({ ...empty, ...p });
    setEditing(p.id);
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, unit_price: Number(form.unit_price) || 0 };
      if (editing) await api.put(`/products/${editing}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Producto actualizado" : "Producto creado");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggle = async (p) => {
    await api.patch(`/products/${p.id}/active`, { active: !p.active });
    toast.success(p.active ? "Producto dado de baja" : "Producto reactivado");
    load();
  };

  const remove = async () => {
    if (!deleteOpen) return;
    await api.delete(`/products/${deleteOpen.id}`);
    toast.success("Producto eliminado");
    setDeleteOpen(null);
    load();
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <>
      <PageHeader
        overline="Catálogo"
        title="Productos"
        action={<Btn onClick={openNew} data-testid="new-product-button"><Plus size={16} /> Nuevo producto</Btn>}
      />
      <div className="p-10">
        {products.length === 0 ? (
          <EmptyState
            title="Sin productos aún"
            subtitle="Da de alta tus productos o servicios para usarlos en cotizaciones, remisiones y facturas."
            action={<Btn onClick={openNew}><Plus size={16} /> Nuevo producto</Btn>}
          />
        ) : (
          <div className="border border-[#E3E8EE] bg-white data-scroll">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E3E8EE] text-left">
                  <Th>Producto</Th><Th>SKU</Th><Th>Unidad</Th>
                  <Th className="text-right">Precio</Th><Th>Estado</Th><Th className="text-right">Acciones</Th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} data-testid={`product-row-${p.id}`} className={`border-b border-[#E3E8EE] last:border-0 hover:bg-[#E9EEF3] transition-colors ${p.active === false ? "opacity-50" : ""}`}>
                    <Td className="text-[#16202B] font-medium">{p.name}</Td>
                    <Td>{p.sku || "—"}</Td>
                    <Td>{p.unit || "—"}</Td>
                    <Td className="text-right text-[#16202B]">{mxn(p.unit_price)}</Td>
                    <Td>
                      <span className={`inline-flex items-center border px-2 py-0.5 text-xs uppercase tracking-wider ${p.active === false ? "bg-stone-200 text-stone-600 border-stone-300" : "bg-[#49AC9A]/15 text-[#49AC9A] border-[#49AC9A]/30"}`}>
                        {p.active === false ? "Baja" : "Activo"}
                      </span>
                    </Td>
                    <Td className="text-right whitespace-nowrap">
                      <button onClick={() => toggle(p)} title={p.active === false ? "Reactivar" : "Dar de baja"} data-testid={`toggle-product-${p.id}`} className="text-[#5B6672] hover:text-[#FDC50D] mx-1.5"><Power size={15} /></button>
                      <button onClick={() => openEdit(p)} title="Editar" data-testid={`edit-product-${p.id}`} className="text-[#5B6672] hover:text-[#465B7F] mx-1.5"><Pencil size={15} /></button>
                      <button onClick={() => setDeleteOpen(p)} title="Eliminar" data-testid={`delete-product-${p.id}`} className="text-[#5B6672] hover:text-[#ED1F26] mx-1.5"><Trash2 size={15} /></button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Editar producto" : "Nuevo producto"}>
        <form onSubmit={save} className="space-y-4">
          <Input label="Nombre *" value={form.name} onChange={set("name")} required testid="product-name" />
          <div className="grid grid-cols-3 gap-4">
            <Input label="SKU" value={form.sku} onChange={set("sku")} testid="product-sku" />
            <Input label="Unidad" value={form.unit} onChange={set("unit")} testid="product-unit" />
            <Input label="Precio *" type="number" step="any" value={form.unit_price} onChange={set("unit_price")} required testid="product-price" />
          </div>
          <label className="block">
            <span className="overline block mb-1.5">Descripción</span>
            <textarea value={form.description} onChange={set("description")} rows={2} data-testid="product-description"
              className="w-full bg-white border border-[#E3E8EE] px-3 py-2 font-mono-data text-sm focus:outline-none focus:ring-1 focus:ring-[#465B7F]" />
          </label>
          <div className="flex justify-end gap-3">
            <Btn variant="ghost" type="button" onClick={() => setOpen(false)}>Cancelar</Btn>
            <Btn type="submit" data-testid="save-product-button">Guardar</Btn>
          </div>
        </form>
      </Modal>

      <ConfirmDialog open={!!deleteOpen} onClose={() => setDeleteOpen(null)} onConfirm={remove}
        title="Eliminar producto" message={`¿Eliminar ${deleteOpen?.name}? Considera darlo de baja si ya se usó en documentos.`}
        confirmLabel="Eliminar producto" testid="confirm-delete-product-button" />
    </>
  );
}

function Th({ children, className = "" }) {
  return <th className={`overline font-normal px-4 py-3 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`font-mono-data text-sm text-[#5B6672] px-4 py-3 ${className}`}>{children}</td>;
}
